import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronRight,
  ArrowLeft,
  AlertCircle,
  Play,
  Loader2,
  Pencil,
} from 'lucide-react';
import { api, ApiError } from '@/api/client';
import type {
  CreateReq,
  CreateResp,
  PreviewReq,
  PreviewResp,
  ResourceType,
} from '@/api/types';
import {
  parseMemoryBytes,
  formatBytes,
  parseStringListItems,
  parseCollectionLive,
  toBase64,
  validateResourceName,
} from '@/lib/resource-helpers';
import TypePicker, { RESOURCE_TYPES } from '@/components/resources/TypePicker';
import Section from '@/components/resources/form/Section';
import Field from '@/components/resources/form/Field';
import SoftLimitField from '@/components/resources/form/SoftLimitField';
import ToggleChip from '@/components/resources/form/ToggleChip';
import CheckboxRow from '@/components/resources/form/CheckboxRow';
import PreviewResult from '@/components/resources/form/PreviewResult';
import CodeField from '@/components/resources/form/CodeField';
import { inputClass, textareaClass } from '@/components/resources/form/styles';
import { extractApiMessage } from '@/components/resources/form/errors';

const DEFAULT_MEASUREMENT_IMAGE = 'registry.suse.com/bci/bci-minimal:latest';
const DEFAULT_MEASUREMENT_INTERVAL = '30';
const DEFAULT_MEASUREMENT_TIMEOUT = '60';

export default function CreateResourcePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Type drives every dynamic section below it. Kept null until the user
  // explicitly picks one — the form is hidden until then.
  const [type, setType] = useState<ResourceType | null>(null);
  // After a type is picked the picker collapses to a one-line summary so the
  // input fields move above the fold (RM-120). "Change" flips this back on
  // to re-show the full grid.
  const [changingType, setChangingType] = useState(false);

  // Common
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [value, setValue] = useState('');
  const [softLimit, setSoftLimit] = useState('');

  // Measurement-only
  const [measurementContainer, setMeasurementContainer] = useState(
    DEFAULT_MEASUREMENT_IMAGE,
  );
  const [measurementInterval, setMeasurementInterval] = useState(
    DEFAULT_MEASUREMENT_INTERVAL,
  );
  const [measurementMaxValue, setMeasurementMaxValue] = useState('');
  const [measurementTimeout, setMeasurementTimeout] = useState(
    DEFAULT_MEASUREMENT_TIMEOUT,
  );
  // Measurement script editor — syntax highlighting on by default; turning
  // it off renders a plain textarea (and never loads the CodeMirror chunk).
  const [scriptHighlight, setScriptHighlight] = useState(true);

  // Collection-only
  const [collectionFormat, setCollectionFormat] = useState<'yaml' | 'json'>(
    'yaml',
  );
  const [shareable, setShareable] = useState(false);
  const [uniform, setUniform] = useState(false);

  // Switching type clears type-specific fields so a stale value from a
  // previous selection can't sneak into the next submit. Name + description
  // are preserved deliberately.
  const handlePickType = (next: ResourceType) => {
    if (next === type) return;
    setType(next);
    setValue('');
    setSoftLimit('');
    setMeasurementContainer(DEFAULT_MEASUREMENT_IMAGE);
    setMeasurementInterval(DEFAULT_MEASUREMENT_INTERVAL);
    setMeasurementMaxValue('');
    setMeasurementTimeout(DEFAULT_MEASUREMENT_TIMEOUT);
    setCollectionFormat('yaml');
    setShareable(false);
    setUniform(false);
    setPreview(null);
  };

  const [preview, setPreview] = useState<PreviewResp | null>(null);

  const createMutation = useMutation({
    mutationFn: (req: CreateReq) =>
      api<CreateResp>('/v1/resources', {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources'] });
      navigate('/resources');
    },
  });

  const previewMutation = useMutation({
    mutationFn: (req: PreviewReq) =>
      api<PreviewResp>('/v1/measurement-preview', {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    onSuccess: (resp) => setPreview(resp),
    onError: (err) => {
      if (err instanceof ApiError && err.body && typeof err.body === 'object') {
        setPreview(err.body as PreviewResp);
      } else {
        setPreview({
          error: true,
          errorMessage: err instanceof Error ? err.message : String(err),
        });
      }
    },
  });

  const nameError = useMemo(() => validateResourceName(name), [name]);

  // Type-specific live validation feedback. Returns null when no issue,
  // otherwise an inline message. We're permissive: warnings are shown but
  // don't block submit (server is authoritative for hard validation).
  const valueValidation = useMemo<{
    error?: string;
    hint?: string;
    okHint?: string;
  }>(() => {
    if (!type || value === '') return {};
    switch (type) {
      case 'LicenseCounter': {
        const n = Number(value.trim());
        if (!Number.isInteger(n)) return { error: 'Must be a whole number' };
        if (n < 0) return { error: 'Must be ≥ 0' };
        return { okHint: `Will be stored as ${n}` };
      }
      case 'Double': {
        const n = Number(value.trim());
        if (!Number.isFinite(n)) return { error: 'Must be a number' };
        if (n < 0) return { error: 'Must be ≥ 0' };
        return { okHint: `Will be stored as ${n}` };
      }
      case 'Memory': {
        const bytes = parseMemoryBytes(value);
        if (bytes === null)
          return { error: 'Expected a value like 1GB, 512MiB, or 1024' };
        return { okHint: `≈ ${formatBytes(bytes)} bytes` };
      }
      case 'StringList': {
        const items = parseStringListItems(value);
        if (items.length === 0) return { error: 'At least one item required' };
        return {
          okHint: `${items.length} item${items.length === 1 ? '' : 's'}`,
        };
      }
      case 'Collection': {
        const parsed = parseCollectionLive(value, collectionFormat);
        if (parsed.error) return { error: parsed.error };
        const uniformHint = uniform
          ? parsed.uniform === false
            ? '⚠ items do not appear uniform (will be rejected on save)'
            : '✓ items appear uniform'
          : '';
        return {
          okHint:
            `${parsed.items.length} item${parsed.items.length === 1 ? '' : 's'}` +
            (uniformHint ? ` · ${uniformHint}` : ''),
        };
      }
      default:
        return {};
    }
  }, [type, value, collectionFormat, uniform]);

  // Soft limit cross-validation — must be 0 < sl < value where applicable.
  const softLimitError = useMemo<string | null>(() => {
    if (!type || softLimit === '') return null;
    if (type === 'LicenseCounter') {
      const v = Number(value.trim());
      const sl = Number(softLimit.trim());
      if (!Number.isInteger(sl) || sl <= 0) return 'Must be a positive integer';
      if (Number.isInteger(v) && sl >= v)
        return `Must be smaller than the resource value (${v})`;
      return null;
    }
    if (type === 'Double') {
      const v = Number(value.trim());
      const sl = Number(softLimit.trim());
      if (!Number.isFinite(sl) || sl <= 0) return 'Must be a positive number';
      if (Number.isFinite(v) && sl >= v)
        return `Must be smaller than the resource value (${v})`;
      return null;
    }
    if (type === 'Memory') {
      const sl = parseMemoryBytes(softLimit);
      const v = parseMemoryBytes(value);
      if (sl === null || sl <= 0)
        return 'Expected a positive memory value (e.g. 256MB)';
      if (v !== null && sl >= v)
        return `Must be smaller than the resource value`;
      return null;
    }
    if (type === 'Measurement') {
      const sl = Number(softLimit.trim());
      const max = Number(measurementMaxValue.trim());
      if (!Number.isInteger(sl) || sl <= 0) return 'Must be a positive integer';
      if (Number.isInteger(max) && max > 0 && sl >= max)
        return `Must be smaller than the maximum value (${max})`;
      return null;
    }
    return null;
  }, [type, softLimit, value, measurementMaxValue]);

  const measurementErrors = useMemo<Partial<Record<string, string>>>(() => {
    if (type !== 'Measurement') return {};
    const errs: Partial<Record<string, string>> = {};
    if (!measurementContainer.trim()) {
      errs.container = 'Container image is required';
    }
    if (measurementInterval !== '') {
      const n = Number(measurementInterval.trim());
      if (!Number.isInteger(n) || n < 0) {
        errs.interval = 'Must be a non-negative integer (0 to disable schedule)';
      }
    }
    if (measurementTimeout !== '') {
      const n = Number(measurementTimeout.trim());
      if (!Number.isInteger(n) || n < 0)
        errs.timeout = 'Must be a non-negative integer';
    }
    if (measurementMaxValue !== '') {
      const n = Number(measurementMaxValue.trim());
      if (!Number.isInteger(n) || n < 0) errs.max = 'Must be a non-negative integer';
    }
    return errs;
  }, [
    type,
    measurementContainer,
    measurementInterval,
    measurementTimeout,
    measurementMaxValue,
  ]);

  const canSubmit = useMemo(() => {
    if (!type || createMutation.isPending) return false;
    if (nameError) return false;
    if (softLimitError) return false;
    // String accepts empty (the old UI's textarea was required, but the v1
    // API has no validator for String — keep parity with the API and let it
    // through). Everything else requires a value.
    if (type !== 'String' && value.trim() === '') return false;
    if (valueValidation.error) return false;
    if (type === 'Measurement') {
      if (Object.keys(measurementErrors).length > 0) return false;
    }
    return true;
  }, [
    type,
    nameError,
    softLimitError,
    value,
    valueValidation.error,
    measurementErrors,
    createMutation.isPending,
  ]);

  // Human-readable reasons the submit button is disabled, from the same
  // checks as canSubmit. Rendered as a summary by the action button so a
  // disabled button always explains itself (RM-120). Gated on the user
  // having started the form so a freshly-picked type isn't nagged.
  const blockers = useMemo<string[]>(() => {
    if (!type) return [];
    const out: string[] = [];
    if (nameError) out.push(nameError);
    if (type !== 'String' && value.trim() === '') {
      const noun =
        type === 'StringList'
          ? 'Items'
          : type === 'Collection'
            ? 'Payload'
            : type === 'Measurement'
              ? 'Script'
              : 'Value';
      out.push(`${noun} is required`);
    }
    if (valueValidation.error) out.push(valueValidation.error);
    if (softLimitError) out.push(`Soft limit: ${softLimitError}`);
    if (type === 'Measurement') {
      for (const m of Object.values(measurementErrors)) if (m) out.push(m);
    }
    return out;
  }, [type, nameError, value, valueValidation.error, softLimitError, measurementErrors]);

  const showBlockers =
    !!type &&
    !canSubmit &&
    !createMutation.isPending &&
    blockers.length > 0 &&
    (name.trim() !== '' || value.trim() !== '');

  const buildPayload = (): CreateReq | null => {
    if (!type) return null;
    const base: CreateReq = {
      name: name.trim(),
      type,
      description: description.trim() || undefined,
    };
    switch (type) {
      case 'LicenseCounter':
      case 'Double':
      case 'StringList':
      case 'Memory':
        base.value = value.trim();
        if (softLimit.trim()) base.softLimit = softLimit.trim();
        return base;
      case 'String':
        // String, like Collection and Measurement, is base64-decoded by
        // its converter on the server side
        // (internal/db/resource/converter/stringconverter.go:32). Send
        // base64 — the StringValidator is permissive and doesn't check
        // the wire format, but the converter rejects raw text with
        // "illegal base64 data".
        base.value = toBase64(value);
        return base;
      case 'Collection':
        // The server's CollectionConverter base64-decodes Value before
        // parsing — see internal/db/resource/converter/collectionconverter.go.
        base.value = toBase64(value);
        base.jsonCollection = collectionFormat === 'json';
        base.shareableCollection = shareable;
        if (uniform) base.uniformCollection = true;
        return base;
      case 'Measurement': {
        base.value = toBase64(value);
        base.measurementContainer = measurementContainer.trim();
        if (measurementInterval.trim() !== '') {
          base.measurementIntervalInSeconds = Number(measurementInterval.trim());
        }
        if (measurementTimeout.trim() !== '') {
          base.measurementTimeout = Number(measurementTimeout.trim());
        }
        if (measurementMaxValue.trim() !== '') {
          base.measurementMaxValue = Number(measurementMaxValue.trim());
        }
        if (softLimit.trim()) base.softLimit = softLimit.trim();
        return base;
      }
    }
    return base;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = buildPayload();
    if (!payload) return;
    createMutation.mutate(payload);
  };

  const handlePreview = () => {
    if (type !== 'Measurement') return;
    const req: PreviewReq = {
      measurementName: name.trim() || 'preview',
      script: toBase64(value),
      image: measurementContainer.trim(),
      timeout: measurementTimeout.trim() || DEFAULT_MEASUREMENT_TIMEOUT,
    };
    previewMutation.mutate(req);
  };

  const submitError = createMutation.error
    ? extractApiMessage(createMutation.error)
    : null;

  const selectedDef = RESOURCE_TYPES.find((t) => t.key === type);
  const SelectedIcon = selectedDef?.icon;

  return (
    <div className="space-y-6">
      {/* Breadcrumb + title row */}
      <div className="space-y-2">
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-1.5 text-sm text-muted-foreground"
        >
          <Link
            to="/resources"
            className="hover:text-foreground transition-colors inline-flex items-center gap-1"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Resources
          </Link>
          <ChevronRight className="h-3.5 w-3.5 opacity-60" aria-hidden />
          <span className="text-foreground">New</span>
        </nav>
        <h1 className="text-2xl font-semibold">Create Resource</h1>
        <p className="text-sm text-muted-foreground">
          Pick a resource type, then fill in the fields below. Allocations,
          limits, and quotas can be configured after the resource exists.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6" noValidate>
        {/* Type picker — collapses to a one-line summary once a type is
            chosen so the input fields move above the fold (RM-120). The
            "Change" button reopens the full grid. */}
        <Section title="Step 1 · Type" description="What kind of resource is this?">
          {!type || changingType ? (
            <TypePicker
              selected={type}
              onSelect={(t) => {
                setChangingType(false);
                handlePickType(t);
              }}
              disabled={createMutation.isPending}
            />
          ) : (
            <div className="flex items-center gap-3">
              {SelectedIcon && (
                <div className="shrink-0 rounded-md p-2 bg-primary text-primary-foreground">
                  <SelectedIcon className="h-5 w-5" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm">
                  {selectedDef?.label ?? type}
                </div>
                {selectedDef?.description && (
                  <div className="text-xs text-muted-foreground truncate">
                    {selectedDef.description}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setChangingType(true)}
                disabled={createMutation.isPending}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Pencil className="h-3.5 w-3.5" />
                Change
              </button>
            </div>
          )}
        </Section>

        {type && (
          <>
            <Section
              title="Step 2 · Identity"
              description={`Naming for the ${selectedDef?.label ?? type} resource.`}
            >
              <div className="grid gap-4 md:grid-cols-2">
                <Field
                  label="Name"
                  htmlFor="name"
                  error={name.length > 0 ? nameError : null}
                  help="No spaces, slashes, or leading/trailing whitespace. Max 255 characters."
                  required
                >
                  <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={`my-${type.toLowerCase()}`}
                    className={inputClass(name.length > 0 && !!nameError)}
                    autoFocus
                    autoComplete="off"
                    spellCheck={false}
                    disabled={createMutation.isPending}
                  />
                </Field>
                <Field
                  label="Description"
                  htmlFor="description"
                  help="Optional. Shown alongside the resource in the list and to API consumers."
                >
                  <input
                    id="description"
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="What does this resource represent?"
                    className={inputClass(false)}
                    disabled={createMutation.isPending}
                  />
                </Field>
              </div>
            </Section>

            <Section
              title={`Step 3 · ${selectedDef?.label ?? type} configuration`}
              description={selectedDef?.description}
            >
              {renderTypeFields()}
            </Section>

            {submitError && (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium">Failed to create resource</div>
                  <div className="text-xs mt-0.5 opacity-90">{submitError}</div>
                </div>
              </div>
            )}

            {showBlockers && (
              <div
                role="status"
                aria-live="polite"
                className="rounded-md border bg-muted/40 p-3 text-sm"
              >
                <div className="flex items-start gap-2">
                  <AlertCircle
                    className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground"
                    aria-hidden
                  />
                  <div>
                    <div className="font-medium">Can’t create yet</div>
                    <ul className="mt-1 text-xs text-muted-foreground list-disc pl-4 space-y-0.5">
                      {blockers.map((b, i) => (
                        <li key={i}>{b}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              <Link
                to="/resources"
                className="inline-flex items-center px-4 py-2 rounded-md text-sm border hover:bg-muted transition-colors"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
              >
                {createMutation.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Create resource
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );

  // Internal renderer — keeps the per-type field markup local so it can
  // close over the page's state setters without prop-drilling. Type narrows
  // on `type` so each branch is checked independently.
  function renderTypeFields() {
    if (!type) return null;
    switch (type) {
      case 'LicenseCounter':
        return (
          <div className="space-y-4">
            <Field
              label="Total available"
              htmlFor="value"
              required
              error={valueValidation.error ?? null}
              hint={valueValidation.okHint ?? null}
              help="Non-negative integer. Allocations count down from this total."
            >
              <input
                id="value"
                type="text"
                inputMode="numeric"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="100"
                className={inputClass(!!valueValidation.error)}
                disabled={createMutation.isPending}
              />
            </Field>
            <SoftLimitField
              value={softLimit}
              onChange={setSoftLimit}
              error={softLimitError}
              placeholder="80"
              help="Optional. Allocations above this value will emit warnings."
              disabled={createMutation.isPending}
            />
          </div>
        );
      case 'String':
        return (
          <Field
            label="Value"
            htmlFor="value"
            help="The literal string returned to every request. Configurations, URLs, env-var snippets — anything serializable."
            required
          >
            <textarea
              id="value"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="PATH=/opt/foo/bin:$PATH"
              rows={4}
              className={textareaClass(false)}
              disabled={createMutation.isPending}
            />
          </Field>
        );
      case 'StringList':
        return (
          <Field
            label="Items (space-separated)"
            htmlFor="value"
            error={valueValidation.error ?? null}
            hint={valueValidation.okHint ?? null}
            help="One pool, each space-separated token is an allocatable item."
            required
          >
            <textarea
              id="value"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="gpu-1 gpu-2 gpu-3"
              rows={3}
              className={textareaClass(!!valueValidation.error)}
              disabled={createMutation.isPending}
            />
          </Field>
        );
      case 'Double':
        return (
          <div className="space-y-4">
            <Field
              label="Total available"
              htmlFor="value"
              required
              error={valueValidation.error ?? null}
              hint={valueValidation.okHint ?? null}
              help="Non-negative number. Fractional values are allowed (e.g. 12.5)."
            >
              <input
                id="value"
                type="text"
                inputMode="decimal"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="3.14"
                className={inputClass(!!valueValidation.error)}
                disabled={createMutation.isPending}
              />
            </Field>
            <SoftLimitField
              value={softLimit}
              onChange={setSoftLimit}
              error={softLimitError}
              placeholder="2.5"
              help="Optional. Allocations above this value will emit warnings."
              disabled={createMutation.isPending}
            />
          </div>
        );
      case 'Memory':
        return (
          <div className="space-y-4">
            <Field
              label="Total available"
              htmlFor="value"
              required
              error={valueValidation.error ?? null}
              hint={valueValidation.okHint ?? null}
              help="Accepts human units (1GB, 512MiB) or raw bytes. All suffixes are treated as binary by the default Ram converter."
            >
              <input
                id="value"
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="64GiB"
                className={inputClass(!!valueValidation.error)}
                disabled={createMutation.isPending}
              />
            </Field>
            <SoftLimitField
              value={softLimit}
              onChange={setSoftLimit}
              error={softLimitError}
              placeholder="48GiB"
              help="Optional. Allocations above this size will emit warnings."
              disabled={createMutation.isPending}
            />
          </div>
        );
      case 'Collection':
        return (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <Field
                label="Format"
                htmlFor="format-yaml"
                help="Wire format of the payload below. YAML is the default for most operators."
              >
                <div className="flex gap-2">
                  <ToggleChip
                    id="format-yaml"
                    label="YAML"
                    selected={collectionFormat === 'yaml'}
                    onClick={() => setCollectionFormat('yaml')}
                    disabled={createMutation.isPending}
                  />
                  <ToggleChip
                    id="format-json"
                    label="JSON"
                    selected={collectionFormat === 'json'}
                    onClick={() => setCollectionFormat('json')}
                    disabled={createMutation.isPending}
                  />
                </div>
              </Field>
              <Field
                label="Shareable"
                htmlFor="shareable"
                help="When set, allocations don't deplete the supply — every consumer can take any item."
              >
                <CheckboxRow
                  id="shareable"
                  checked={shareable}
                  onChange={setShareable}
                  label="Allocations don't deplete supply"
                  disabled={createMutation.isPending}
                />
              </Field>
              <Field
                label="Uniform"
                htmlFor="uniform"
                help="When set, every item must share the same top-level key set. Validated at save."
              >
                <CheckboxRow
                  id="uniform"
                  checked={uniform}
                  onChange={setUniform}
                  label="Items share the same keys"
                  disabled={createMutation.isPending}
                />
              </Field>
            </div>
            <Field
              label="Payload"
              htmlFor="value"
              required
              error={valueValidation.error ?? null}
              hint={valueValidation.okHint ?? null}
              help={
                collectionFormat === 'yaml'
                  ? 'YAML block sequence. Each "- " starts a new item.'
                  : 'JSON array of items. Each element becomes one allocatable item.'
              }
            >
              <CodeField
                language={collectionFormat === 'json' ? 'json' : 'yaml'}
                value={value}
                onChange={setValue}
                editable={!createMutation.isPending}
                invalid={!!valueValidation.error}
                rows={10}
                placeholder={
                  collectionFormat === 'yaml'
                    ? '- host: node-01\n  cpu: 64\n- host: node-02\n  cpu: 96'
                    : '[\n  {"host": "node-01", "cpu": 64},\n  {"host": "node-02", "cpu": 96}\n]'
                }
                ariaLabel="Collection payload"
                data-testid="value-editor"
              />
            </Field>
          </div>
        );
      case 'Measurement':
        return (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Field
                label="Container image"
                htmlFor="container"
                required
                error={measurementErrors.container ?? null}
                help="Image the script runs in."
              >
                <input
                  id="container"
                  type="text"
                  value={measurementContainer}
                  onChange={(e) => setMeasurementContainer(e.target.value)}
                  className={inputClass(!!measurementErrors.container)}
                  disabled={createMutation.isPending}
                />
              </Field>
              <Field
                label="Interval (s)"
                htmlFor="interval"
                error={measurementErrors.interval ?? null}
                help="How often the script runs. 0 disables the schedule."
              >
                <input
                  id="interval"
                  type="text"
                  inputMode="numeric"
                  value={measurementInterval}
                  onChange={(e) => setMeasurementInterval(e.target.value)}
                  className={inputClass(!!measurementErrors.interval)}
                  disabled={createMutation.isPending}
                />
              </Field>
              <Field
                label="Max value"
                htmlFor="max"
                error={measurementErrors.max ?? null}
                help="Upper bound for allocations. Leave blank for unbounded."
              >
                <input
                  id="max"
                  type="text"
                  inputMode="numeric"
                  value={measurementMaxValue}
                  onChange={(e) => setMeasurementMaxValue(e.target.value)}
                  className={inputClass(!!measurementErrors.max)}
                  placeholder="(unbounded)"
                  disabled={createMutation.isPending}
                />
              </Field>
              <Field
                label="Timeout (s)"
                htmlFor="timeout"
                error={measurementErrors.timeout ?? null}
                help="How long a single run is allowed to take."
              >
                <input
                  id="timeout"
                  type="text"
                  inputMode="numeric"
                  value={measurementTimeout}
                  onChange={(e) => setMeasurementTimeout(e.target.value)}
                  className={inputClass(!!measurementErrors.timeout)}
                  disabled={createMutation.isPending}
                />
              </Field>
            </div>
            <SoftLimitField
              value={softLimit}
              onChange={setSoftLimit}
              error={softLimitError}
              placeholder="80"
              help="Optional. Integer threshold; warnings when measured value crosses it. Must be smaller than the max value (when max is set)."
              disabled={createMutation.isPending}
            />
            <Field
              label="Script"
              htmlFor="value"
              required
              help="Sent base64-encoded. First line is typically `#!/bin/bash`. Use Preview to run before saving."
            >
              <div className="mb-1.5 flex justify-end">
                <label className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground select-none cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scriptHighlight}
                    onChange={(e) => setScriptHighlight(e.target.checked)}
                    className="rounded border-border"
                    disabled={createMutation.isPending}
                  />
                  Syntax highlighting
                </label>
              </div>
              <CodeField
                highlight={scriptHighlight}
                language="shell"
                value={value}
                onChange={setValue}
                editable={!createMutation.isPending}
                rows={10}
                placeholder={`#!/bin/bash\n# Print a single integer to stdout\necho 42`}
                ariaLabel="Measurement script"
                data-testid="value-editor"
              />
            </Field>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handlePreview}
                disabled={
                  previewMutation.isPending ||
                  !value.trim() ||
                  !measurementContainer.trim()
                }
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {previewMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                Preview run
              </button>
            </div>
            {preview && <PreviewResult resp={preview} />}
          </div>
        );
    }
  }
}

